import React, { useContext, useState, useEffect } from 'react'
import { AuthContext } from '@/layout/Auth'
import {
  Box,
  AppBar,
  Stack,
  Link,
  Button,
  Menu,
  MenuItem,
  Typography,
  Popover,
  TextField,
  InputAdornment,
  Select,
  IconButton,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Message, Icon } from '@cx/ui'
import { useDebounceFn, useRequest } from 'ahooks'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { ALL_TOOLKIT_LIST, SECURITY_HOLE, TOOLKIT_LIST } from '@/constant/toolkit'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import { useLocation } from 'react-router-dom'
import LoggedInView from './loggedInView'
import { getUserOrganizations, setDefaultOrg } from '@/api'
import CreateNewSpaceModal from './CreateNewSpaceModal'

const NAV_LINK = [
  {
    title: '技术讨论',
    href: '/discussion',
  },
  {
    title: '长亭漏洞情报库',
    href: '/vuldb',
    target: '_blank',
  },
  {
    title: 'IP 威胁情报',
    href: '/ip-intelligence',
    target: '_blank',
  },
  {
    title: 'SLA',
    href: 'https://web-monitor.app.safepoint.cloud/sharing/67889cb8-e143-4081-9f3b-0c52eb1b02a2',
    target: '_blank',
  },
  {
    title: '在线工具',
    href: '/tools',
    target: '_blank',
    type: 'menu',
  },
]

const ToolItem = ({ data }: any) => {
  return (
    <Stack gap={2}>
      <Typography variant='h2' sx={{ fontSize: 14, color: '#000', fontWeight: 600 }}>
        {data.name}
      </Typography>
      {data.tools.map((tool: any) => (
        <Box
          key={tool.name}
          sx={{
            fontSize: 12,
            color: 'rgba(0,0,0,0.8)',
            '&:hover': {
              color: 'primary.main',
            },
          }}
          onClick={() => {
            tool.url && window.open(tool.url, '_blank')
          }}
        >
          {tool.name}
        </Box>
      ))}
    </Stack>
  )
}

const Header = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { authInfo: user, updateAuthInfo } = useContext(AuthContext)
  const [afterKeyword, setAfterKeyword] = useState('')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectEl, setSelectEl] = useState<null | HTMLElement>(null)
  const [toolList, setToolList] = useState<any[]>(TOOLKIT_LIST)
  const [keyword, setKeyword] = useState('')
  const [selectOrg, setSelectOrg] = useState(user?.orgInfo)
  const [createNewSpaceModalVisible, setCreateNewSpaceModalVisible] = useState(false)
  const { data: orgData, refresh: refreshGetOrganizations } = useRequest(getUserOrganizations)
  const open = Boolean(anchorEl)
  const selectOpen = Boolean(selectEl)

  const { run: runSearch } = useDebounceFn(
    (k: string) => {
      handleSearch(k.trim())
    },
    { wait: 500 },
  )

  const handleSearch = (k: string) => {
    if (k === '') {
      setToolList(TOOLKIT_LIST)
      setAfterKeyword(k)
      return
    }
    const toolList = ALL_TOOLKIT_LIST.reduce((prev, cur) => {
      const tools = cur.tools.filter((item) => item.name.toUpperCase().includes(k.toUpperCase()))
      if (tools.length) {
        prev.push({
          name: cur.name,
          tools,
        })
      }
      return prev
    }, [] as any[])

    setToolList(toolList)
    setAfterKeyword(k)
  }

  const onChangeOrg = (orgInfo: any) => {
    return setDefaultOrg({ ID: orgInfo.id }).then(() => {
      updateAuthInfo({ orgInfo })
      Message.success('切换空间成功')
    })
  }

  useEffect(() => {
    runSearch(keyword)
  }, [keyword])

  useEffect(() => {
    if (user?.orgInfo?.id) {
      setSelectOrg(user?.orgInfo)
    }
  }, [user?.orgInfo?.id])

  return (
    <AppBar
      position='fixed'
      sx={{
        display: { xs: 'none', sm: 'block' },
        backgroundColor: '#fff',
        boxShadow: '0px 2px 6px 0px rgba(0,0,0,0.1), 0px 2px 6px 0px rgba(218,220,224,0.5)',

        zIndex: 100,
      }}
    >
      <CreateNewSpaceModal
        open={createNewSpaceModalVisible}
        onOk={(org: any) => {
          onChangeOrg(org)
          refreshGetOrganizations()
        }}
        onCancel={() => {
          setCreateNewSpaceModalVisible(false)
        }}
      />
      <Stack
        direction='row'
        sx={{ height: 64, position: 'relative' }}
        alignItems='center'
        justifyContent='space-between'
      >
        <Stack direction='row' alignItems='center'>
          <Stack direction='row' alignItems='center'>
            <Typography
              variant='h2'
              sx={{
                ml: 5,
                mr: 10,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 700,
                color: '#000',
              }}
              onClick={() => {
                window.open('/', '_self')
              }}
            >
              长亭百川云
            </Typography>

            <Stack direction='row' gap={5} alignItems='center'>
              {NAV_LINK.map((item) => (
                <Link
                  key={item.title}
                  color='#999'
                  underline='none'
                  href={item.href}
                  target='_blank'
                  // onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  //   if (item.type === 'menu') {
                  //     setAnchorEl(e.currentTarget);
                  //   }
                  // }}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    if (item.type === 'menu') {
                      e.preventDefault()
                      e.stopPropagation()
                      setAnchorEl(e.currentTarget)
                    }
                    if (item.type === 'select') {
                      e.preventDefault()
                      e.stopPropagation()
                      setSelectEl(e.currentTarget)
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px',
                    gap: 0.5,
                    color:
                      (open && item.title === '在线工具') ||
                      (selectOpen && item.title === '安全情报') ||
                      pathname === item.href
                        ? 'primary.main'
                        : 'rgba(0,0,0,0.5)',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {item.title}
                  {(item.type === 'menu' || item.type === 'select') && (
                    <PlayArrowRoundedIcon
                      sx={{
                        transition: 'transform 0.3s',
                        transform:
                          (open && item.title === '在线工具') || (selectOpen && item.title === '安全情报')
                            ? 'rotate(-90deg)'
                            : 'rotate(90deg)',
                        color:
                          (open && item.title === '在线工具') || (selectOpen && item.title === '安全情报')
                            ? 'primary.main'
                            : 'rgba(0,0,0,0.5)',
                        mt: '-1px',
                        fontSize: 12,
                      }}
                    />
                  )}
                </Link>
              ))}
            </Stack>
          </Stack>
        </Stack>

        <Menu
          id='basic-menu'
          anchorEl={selectEl}
          open={Boolean(selectEl)}
          onClose={() => {
            setSelectEl(null)
          }}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          sx={{ mt: 2 }}
        >
          {SECURITY_HOLE.map((item) => (
            <MenuItem key={item.name}>
              <Box
                key={item.name}
                sx={{
                  fontSize: 14,
                  color: '#000',
                  pr: 3,
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
                onClick={() => {
                  item.url && window.open(item.url, '_blank')
                }}
              >
                {item.name}
              </Box>
            </MenuItem>
          ))}
        </Menu>
        <Popover
          anchorEl={anchorEl}
          open={open}
          onClose={() => {
            setAnchorEl(null)
          }}
          // sx={{ position: 'static' }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: '100%',
              maxHeight: 700,
              ml: -2,
              mt: 2,
              py: 3,
              px: 9,
              boxShadow: ' 0px 10px 20px 0px rgba(0,28,85,0.04)',
            },
            onMouseLeave: () => {
              // setAnchorEl(null);
            },
          }}
        >
          <Stack direction='row' justifyContent='space-between' alignItems='flex-start' gap={3}>
            <TextField
              sx={{ mb: 3, flex: 1 }}
              autoComplete='off'
              variant='standard'
              placeholder='搜索工具'
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value)
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchRoundedIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant='contained' size='small' href='/toolkit' sx={{ boxShadow: 'none !important' }}>
              更多工具
            </Button>
          </Stack>

          <Stack direction='row'>
            {afterKeyword === '' && toolList.length === 10 && (
              <>
                <Stack gap={6} sx={{ width: '12.5%' }}>
                  <ToolItem data={toolList[0]}></ToolItem>
                  <ToolItem data={toolList[1]}></ToolItem>
                </Stack>
                <Stack gap={6} sx={{ width: '12.5%' }}>
                  <ToolItem data={toolList[2]}></ToolItem>
                  <ToolItem data={toolList[3]}></ToolItem>
                </Stack>
              </>
            )}

            {toolList.slice(afterKeyword === '' && toolList.length === 10 ? 4 : 0).map((item) => (
              <Box sx={{ width: '12.5%' }} key={item.name}>
                <ToolItem data={item}></ToolItem>
              </Box>
            ))}
          </Stack>
          {toolList.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                backgroundColor: '#fff',
                borderRadius: 2,
              }}
            >
              <Box component='img' sx={{ width: 300 }} src='/empty.png' alt='empty' />
              <Typography sx={{ fontWeight: 600 }}>未找到你查询的内容</Typography>
            </Box>
          )}
        </Popover>

        <Stack direction='row' alignItems='center' sx={{ position: 'absolute', top: 0, bottom: 0, right: 0 }}>
          {user ? (
            <Stack alignItems='center' direction='row' gap={3} sx={{ pr: 5 }}>
              {pathname !== '/console/workbench' && (
                <Button
                  variant='contained'
                  sx={{ width: 123, height: 44, boxShadow: 'none', borderRadius: 1 }}
                  onClick={() => {
                    window.open('/console/workbench', '_self')
                  }}
                >
                  工作台
                </Button>
              )}
              <Select
                size='small'
                value={selectOrg?.id || ''}
                sx={{
                  height: 36,
                  width: 247,
                  alignItems: 'center',
                  borderRadius: 2,
                  '.workbench-app-icon': { color: '#A0C9FF' },
                  '.workbench-app-icon-wrapper': {
                    border: 'none',
                    background: 'none',
                  },
                  '.MuiSelect-outlined': {
                    py: 0,
                  },
                }}
                MenuProps={{
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  sx: {
                    '.MuiList-root': {
                      p: 2,
                      borderRadius: 2,
                    },
                  },
                }}
              >
                {orgData?.data?.map((item) => (
                  <MenuItem
                    key={item.id}
                    value={item.id}
                    onClick={() => onChangeOrg(item)}
                    sx={{
                      mb: 0.5,
                      width: 247,
                      justifyContent: 'space-between',
                      borderRadius: 1,
                      '&:hover': {
                        '.workbench-app-edit': {
                          display: 'flex',
                        },
                      },
                      '&.Mui-selected': {
                        color: 'primary.main',
                        '.workbench-app-icon': {
                          color: '#A0C9FF',
                        },
                      },
                    }}
                  >
                    <Stack direction='row' gap={1.5} alignItems='center'>
                      <Stack
                        justifyContent='center'
                        alignItems='center'
                        className='workbench-app-icon-wrapper'
                        sx={{ width: 24, height: 24, border: '1px solid #EEEEEE', background: '#fff', borderRadius: 1 }}
                      >
                        <Icon
                          type='icon-kongjianzuzhi'
                          className='workbench-app-icon'
                          sx={{ color: 'rgba(0,0,0,0.2)' }}
                        />
                      </Stack>

                      <Box
                        sx={{
                          fontSize: 14,
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          maxWidth: 168,
                        }}
                      >
                        {item.name}
                      </Box>
                    </Stack>
                    <IconButton
                      className='workbench-app-edit'
                      sx={{ padding: 0.5, display: 'none' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        onChangeOrg(item).then(() => {
                          navigate('/console/space/base')
                        })
                      }}
                    >
                      <Icon type='icon-guanli' sx={{ fontSize: 12, color: 'primary.main' }} />
                    </IconButton>
                  </MenuItem>
                ))}
                <Button
                  variant='outlined'
                  sx={{ width: '100%', mt: 2 }}
                  onClick={() => {
                    setCreateNewSpaceModalVisible(true)
                  }}
                >
                  + 新建空间
                </Button>
              </Select>
              <LoggedInView />
            </Stack>
          ) : (
            <>
              <Button
                sx={{ borderRadius: 0, px: 3 }}
                onClick={() => {
                  window.open('/login', '_self')
                }}
              >
                登录
              </Button>
              <Button
                variant='contained'
                sx={{ borderRadius: 0, px: 3 }}
                onClick={() => {
                  window.open('/register', '_self')
                }}
              >
                立即注册
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </AppBar>
  )
}

export default Header
